import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@roger/ui-components';
import { Download, Loader2 } from 'lucide-react';
import {
  getKinnserSyncStatus,
  listKinnserAgencyClinics,
  pullKinnserData,
  setKinnserClinicLeftOut,
  writeKinnserClinicIdentity,
} from '../../services/api';
import {
  getUserFacingApiMessage,
  getValidationErrorItems,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import type { AgencyClinicIdentity } from '../../services/api/types.gen';
import { rowDisplayName, type AgencyDataFillRow } from './agencyDataFillRows';
import {
  formatMoment,
  isKinnserOff,
  KINNSER_OFF_HINT,
} from './agencyDataFillView';
import { applySummary } from './kinnserApplyCounts';
import { pulledByClinicKey } from './kinnserPulled';
import { AutoSavePill } from '../AutoSavePill';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { KinnserIdentityGrid } from './KinnserIdentityGrid';
import { rosterCounts, type RosterCounts } from './kinnserRoster';

/** How long "Saved" stays up before the pill goes back to saying nothing. */
const SAVED_PILL_MS = 2500;

/**
 * One agency, beside the list rather than under the row that named it.
 *
 * The pane scrolls on its own, so picking an agency cannot move the page --
 * which is what an expanding row did, by reserving height under itself for
 * whichever row was open.
 *
 * Pull is here, at the top beside the agency's name, rather than down beside
 * the line that says what the last one got: it is the thing an operator comes
 * to this screen to do.
 *
 * One button, not two. Pulling and filling were never separate decisions --
 * the nightly sync applies every agency's stored payload anyway -- and the gap
 * between them is what put two versions of every value on this screen.
 */

const SOURCE_LABELS: Record<string, string> = {
  schedule: 'Nightly sync',
  post_scrape: 'RogerDoc scrape',
  internal: 'Internal route',
  button: 'Admin page button',
  goals_resync: 'Goals resync',
  create_agency: 'Agency creation',
  operator: 'Operator',
  demo: 'Sandbox build',
};

/** Who asked for a sync, in words rather than the stored enum value. */
function sourceLabel(source: string | null | undefined): string | null {
  if (!source) return null;
  return SOURCE_LABELS[source] ?? source;
}

const UPSTREAM_EMPTY =
  'Kinnser returned no clinics for this agency. Nothing was written — check ' +
  'the agency’s Kinnser credentials.';

/**
 * Why a save was refused, in the words the validator used.
 *
 * ``getBusinessErrorMessage`` is the wrong helper here twice over. A field
 * validator's refusal arrives as a Pydantic 422 *array*, which it answers null
 * for, and the 502 for an empty Kinnser read is a 5xx, which it also answers
 * null for -- so every reason this screen was built to show would have come
 * out as "please try again".
 *
 * Surfacing the server's own wording is allowed here because this is an
 * internal tool behind the Strawhat gate, and the wording is what makes the
 * refusal actionable: "NPI must be exactly 10 digits" is the whole message.
 *
 * The 422 array's ``loc`` is dropped rather than rendered: it reads
 * ``body.npi``, and the message is already sitting in the NPI cell.
 */
function refusalMessage(caught: unknown, fallback: string): string {
  const [first] = getValidationErrorItems(caught);
  if (first?.msg) return first.msg;
  // A 502 here means one thing: Kinnser answered with no clinics. Said in our
  // own words rather than by rendering the server's, because every helper
  // withholds 5xx detail on purpose and that rule is worth more than this
  // screen -- but "please try again" would send somebody pressing the button
  // again at a problem that is not on our side.
  if (getHttpStatus(caught) === 502) return UPSTREAM_EMPTY;
  return getUserFacingApiMessage(caught, fallback);
}

const Fact: React.FC<{
  label: string;
  failed?: boolean;
  children: React.ReactNode;
}> = ({ label, failed, children }) => (
  <p
    className={`text-xs ${failed ? 'text-destructive' : 'text-muted-foreground'}`}
  >
    <span
      className={`font-medium ${failed ? 'text-destructive' : 'text-foreground'}`}
    >
      {label}:{' '}
    </span>
    {children}
  </p>
);
export const AgencyDataFillPane: React.FC<{
  row: AgencyDataFillRow;
  /** A pull rewrote the agency, so the list's dates and counts are stale. */
  onPulled: () => void;
}> = ({ row, onPulled }) => {
  const [clinics, setClinics] = useState<
    readonly AgencyClinicIdentity[] | null
  >(null);
  const [clinicsFailed, setClinicsFailed] = useState(false);
  // Null until the read lands, and again when it fails: the rows draw dashes.
  const [roster, setRoster] = useState<RosterCounts | null>(null);
  // Which agency the rows on screen belong to, so a re-read can tell a refresh
  // from a switch.
  const shown = useRef<string | null>(null);
  // Bumped to ask again. The effect keys on it, so a retry is one state change
  // rather than a second copy of the fetch.
  const [attempt, setAttempt] = useState(0);
  const agencyId = row.agencyId;
  // The per-agency routes refuse an agency Kinnser is off for, and the rail
  // lists those now. Asking anyway would put "could not load" under the line
  // that just explained there is nothing to load.
  const kinnserOff = isKinnserOff(row);

  useEffect(() => {
    if (kinnserOff) {
      setClinics([]);
      setClinicsFailed(false);
      setRoster(null);
      return;
    }
    let cancelled = false;
    // Blanked only when the agency changed. A re-read after a save keeps the
    // rows on screen: nulling them unmounted every cell, which threw away the
    // sideways scroll an operator had done to reach the clinic, took focus out
    // of the row they were tabbing along, and dropped any edit still waiting to
    // be sent.
    if (agencyId !== shown.current) {
      shown.current = agencyId;
      setClinics(null);
    }
    setClinicsFailed(false);
    // The one-agency read, for the counts under the grid. Its own request and
    // its own failure: the list route does not carry them, and a roster that
    // did not arrive costs three rows of dashes rather than the grid.
    getKinnserSyncStatus({ path: { agency_id: agencyId }, throwOnError: true })
      .then(res => {
        if (!cancelled) setRoster(rosterCounts(res.data));
      })
      .catch(caught => {
        console.error("Failed to read the agency's roster counts", caught);
        if (!cancelled) setRoster(null);
      });
    listKinnserAgencyClinics({
      path: { agency_id: agencyId },
      throwOnError: true,
    })
      .then(res => {
        if (!cancelled) setClinics(res.data ?? []);
      })
      .catch(caught => {
        // No toast: the grid says so in place, and a pane that swaps as you
        // move down the rail would stack one toast per agency.
        console.error("Failed to load the agency's clinics", caught);
        if (!cancelled) setClinicsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId, kinnserOff, attempt]);

  const retryClinics = useCallback(() => setAttempt(n => n + 1), []);
  const reread = useCallback(() => setAttempt(n => n + 1), []);

  const [pulling, setPulling] = useState(false);
  // A count, not a flag: tabbing along a row starts a save per cell and they
  // overlap, so a boolean would clear on the first one that finished while two
  // were still going.
  const [saving, setSaving] = useState(0);
  // What the pill says. Latched, so the outcome of the last save stays on
  // screen after the spinner stops rather than blinking out of existence.
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');
  // Whether anything in the current run of saves was refused. A ref, because
  // the effect below reads it in the same tick the count reaches zero.
  const saveFailed = useRef(false);
  // The previous count, so a run can be told from a save joining one already
  // going. Without it, a second save starting clears the first one's refusal.
  const wasSaving = useRef(0);
  const [pullError, setPullError] = useState<string | null>(null);
  // Keyed by clinic and field, so a refusal sits against the cell that caused
  // it rather than as one message for the whole grid.
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});

  // One place, rather than a set in each of the two save paths: cell saves
  // overlap -- tabbing along a row starts one per cell -- so "finished" is the
  // count reaching zero, not any single request returning.
  useEffect(() => {
    const before = wasSaving.current;
    wasSaving.current = saving;
    if (saving > 0) {
      // Only when a run begins. A save that joins one already in flight moves
      // the count without starting anything, and clearing here would drop a
      // refusal that has already happened in the same run.
      if (before === 0) saveFailed.current = false;
      setSaveStatus('saving');
      return;
    }
    setSaveStatus(was =>
      was === 'saving' ? (saveFailed.current ? 'error' : 'saved') : was
    );
  }, [saving]);

  // "Saved" is news for a moment and then it is furniture, so it goes. A
  // refusal stays: it is the only thing on screen once the cell that caused it
  // has been scrolled away from, and it clears when the next save starts.
  useEffect(() => {
    if (saveStatus !== 'saved') return;
    const timer = window.setTimeout(
      () => setSaveStatus(was => (was === 'saved' ? 'idle' : was)),
      SAVED_PILL_MS
    );
    return () => window.clearTimeout(timer);
  }, [saveStatus]);

  // Nothing carried to the next agency: its cells have their own outcomes.
  useEffect(() => {
    setSaveStatus('idle');
    saveFailed.current = false;
    wasSaving.current = 0;
  }, [agencyId]);

  const pull = useCallback(async () => {
    setPulling(true);
    setPullError(null);
    try {
      await pullKinnserData({
        path: { agency_id: agencyId },
        throwOnError: true,
      });
      onPulled();
      reread();
    } catch (caught) {
      console.error('Failed to pull this agency from Kinnser', caught);
      // The 409 for every clinic left out and the 502 for an empty read both
      // carry the sentence that says what to do next.
      setPullError(
        refusalMessage(caught, 'Unable to pull from Kinnser. Please try again.')
      );
    } finally {
      setPulling(false);
    }
  }, [agencyId, onPulled, reread]);

  /** Put one value on one clinic, on screen, without waiting for anything. */
  const showOn = useCallback(
    (
      clinicId: string,
      field: keyof AgencyClinicIdentity,
      value: string | boolean | null
    ) =>
      setClinics(was =>
        (was ?? []).map(each =>
          each.id === clinicId ? { ...each, [field]: value } : each
        )
      ),
    []
  );

  const editClinic = useCallback(
    async (
      clinic: AgencyClinicIdentity,
      field: keyof AgencyClinicIdentity,
      // A string for a typed cell, a boolean for one of the switches. Both are
      // one column on one row, so they take the same route.
      value: string | boolean
    ) => {
      const at = `${clinic.id}:${field}`;
      if (!clinic.clinic_key) {
        setCellErrors(was => ({
          ...was,
          [at]: 'This clinic has no Kinnser id to save against.',
        }));
        return;
      }
      setCellErrors(was =>
        Object.fromEntries(Object.entries(was).filter(([key]) => key !== at))
      );

      // On screen first, then sent. A tick that waits on a round trip before it
      // moves reads as a click that missed, and the answer is one this already
      // knows. What it does not know is the cleaning the backend does -- a
      // typed "(512) 555-0142" lands as "5125550142" -- so the response still
      // settles the cell afterwards.
      const before = clinic[field];
      const next = value === '' ? null : value;
      showOn(clinic.id, field, next);

      setSaving(n => n + 1);
      try {
        const saved = await writeKinnserClinicIdentity({
          path: { agency_id: agencyId, clinic_key: clinic.clinic_key },
          // Blank clears the column, which is how somebody asks for Kinnser's
          // value back on the next pull.
          body: { [field]: next },
          throwOnError: true,
        });
        const written = saved.data;
        if (written) {
          setClinics(was =>
            (was ?? []).map(each => (each.id === written.id ? written : each))
          );
        }
      } catch (caught) {
        console.error('Failed to save the clinic value', caught);
        saveFailed.current = true;
        // Back to what the agency still holds. Leaving the refused value on
        // screen beside the reason it was refused would read as saved.
        showOn(clinic.id, field, (before ?? null) as string | boolean | null);
        setCellErrors(was => ({
          ...was,
          [at]: refusalMessage(caught, 'Could not save. Please try again.'),
        }));
      } finally {
        setSaving(n => n - 1);
      }
    },
    [agencyId, showOn]
  );

  const leaveOut = useCallback(
    async (clinic: AgencyClinicIdentity, leftOut: boolean) => {
      if (!clinic.clinic_key) return;
      // The switch flips the clinic's is_kinnser flag, which is what the box is
      // drawn from. Flipped here first for the same reason a cell is written
      // first: the tick should not wait on a round trip to move.
      showOn(clinic.id, 'is_kinnser', !leftOut);
      setSaving(n => n + 1);
      try {
        await setKinnserClinicLeftOut({
          path: { agency_id: agencyId, clinic_key: clinic.clinic_key },
          body: { left_out: leftOut },
          throwOnError: true,
        });
        // No refetch: the tick above already moved, and nothing outside this
        // box reads the flag -- rereading the agency only threw the section
        // away and drew it again under whoever was still clicking.
      } catch (caught) {
        console.error('Failed to change the leave-out setting', caught);
        saveFailed.current = true;
        showOn(clinic.id, 'is_kinnser', leftOut);
        setCellErrors(was => ({
          ...was,
          [`${clinic.id}:is_kinnser`]: refusalMessage(
            caught,
            'Could not save. Please try again.'
          ),
        }));
      } finally {
        setSaving(n => n - 1);
      }
    },
    [agencyId, showOn]
  );

  const setAutoMrn = useCallback(
    (clinic: AgencyClinicIdentity, autoMrn: boolean) =>
      void editClinic(clinic, 'auto_mrn', autoMrn),
    [editClinic]
  );

  const errorFor = useCallback(
    (clinicId: string, field: keyof AgencyClinicIdentity) =>
      cellErrors[`${clinicId}:${field}`],
    [cellErrors]
  );

  // What the last pull sent, so a cell can name Kinnser's value under one an
  // operator has changed. Keyed by the Kinnser clinic id, which is the clinic
  // row's own clinic_key.
  const pulled = useMemo(() => pulledByClinicKey(row.status), [row.status]);
  const pulledFor = useCallback(
    (clinic: AgencyClinicIdentity) =>
      clinic.clinic_key ? pulled.get(clinic.clinic_key) : undefined,
    [pulled]
  );

  const readBy = sourceLabel(row.status.fetch_source);
  const filledBy = sourceLabel(row.status.apply_source);
  const wrote = applySummary(row.status.apply_result);

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-y-auto p-4'>
      {/* Sticky to this pane, which is the box that scrolls: the grid is tall
          and wide, so the cell somebody just typed into is usually nowhere near
          the header.

          Zero height, so it is an overlay rather than a row. In the flow it
          pushed the whole pane down the moment a save started and let it back
          up when the pill retired -- the grid jumping under the pointer of
          somebody part-way through correcting it. */}
      <div className='pointer-events-none sticky top-0 z-30 h-0'>
        <div className='flex justify-end'>
          <AutoSavePill status={saveStatus} variant='manual' />
        </div>
      </div>
      {/* A header block rather than a list of facts: the name and the one
          action on it belong on the same line, and the dates under them are
          what that action just changed. A rule closes it, so the grid below
          starts somewhere rather than continuing the same column of grey. */}
      <div className='flex flex-col gap-2 border-b pb-3'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <h3 className='text-base font-semibold'>{rowDisplayName(row)}</h3>
          <div className='flex flex-wrap items-center gap-1'>
            {!kinnserOff && (
              <Button
                size='sm'
                variant='outline'
                aria-label={`Pull ${rowDisplayName(row)} from Kinnser`}
                /* Not while a save is in flight: a pull rewrites every clinic
                 row, and one landing on top of a value still being written
                 decides the race by accident. */
                disabled={pulling || saving > 0}
                onClick={() => void pull()}
              >
                {pulling ? (
                  <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Download className='mr-1 h-3.5 w-3.5' />
                )}
                {pulling ? 'Pulling' : 'Pull from Kinnser'}
              </Button>
            )}
          </div>
        </div>

        {/* Two sentences, not a two-column list. What Kinnser last sent and
            what reached the agency's tables are the two facts every action
            here acts on, and the counts ride on the second rather than in a
            row of their own -- a pull writes, so the question it leaves is
            whether anything moved. */}
        <Fact label='Last read'>
          {row.fetchedAt
            ? `${formatMoment(row.fetchedAt)}${readBy ? ` · ${readBy}` : ''}.`
            : 'Never.'}
        </Fact>
        <Fact label='Last written'>
          {row.appliedAt
            ? `${formatMoment(row.appliedAt)}${
                filledBy ? ` · ${filledBy}` : ''
              }.${wrote ? ` ${wrote}` : ''}`
            : 'Never. Nothing from Kinnser has reached this agency\u2019s tables.'}
        </Fact>
        {pullError && (
          <Fact label='Pull' failed>
            {pullError}
          </Fact>
        )}
        {isKinnserOff(row) && (
          <p className='text-xs text-muted-foreground'>{KINNSER_OFF_HINT}</p>
        )}
      </div>

      <div className='pt-4'>
        {!kinnserOff && (
          <KinnserIdentityGrid
            clinics={clinics}
            clinicsFailed={clinicsFailed}
            onRetry={retryClinics}
            roster={roster}
            onEdit={(clinic, field, value) =>
              void editClinic(clinic, field, value)
            }
            pulledFor={pulledFor}
            errorFor={errorFor}
            onLeftOut={(clinic, leftOut) => void leaveOut(clinic, leftOut)}
            onAutoMrn={setAutoMrn}
          />
        )}
      </div>
    </div>
  );
};
